import type { FolderRecord } from '../../data-binding/models/FolderRecord';

export type AllocationLoadStatus =
  | 'under_allocated'
  | 'fully_allocated'
  | 'over_allocated'
  | 'unknown';

export interface OrgAllocationAssignment {
  readonly relationshipRecordId: string;
  readonly relationshipExternalId: string | null;
  readonly actorExternalId: string;
  readonly actorLabel: string;
  readonly actorKind: string;
  readonly personExternalId: string;
  readonly personLabel: string;
  readonly targetExternalId: string;
  readonly targetLabel: string;
  readonly targetKind: string;
  readonly allocation: number | null;
  readonly hasUnspecifiedAllocation: boolean;
  readonly effort: number | null;
  readonly effortUnit: string | null;
  readonly role: string | null;
  readonly validFrom: string | null;
  readonly validTo: string | null;
  readonly basis: string | null;
  readonly lifecycleState?: string | null;
  readonly recordStatus?: string | null;
  readonly sourceStatus?: string | null;
  readonly sourceBindingStatus?: string | null;
  readonly confidence?: number | null;
  readonly source: Record<string, unknown>;
}

export interface OrgPersonAllocationLoad {
  readonly personExternalId: string;
  readonly personLabel: string;
  readonly totalAllocation: number;
  readonly assignmentCount: number;
  readonly hasUnspecifiedAllocation: boolean;
  readonly status: AllocationLoadStatus;
  readonly assignments: readonly OrgAllocationAssignment[];
}

export interface OrgActorAllocationLoad {
  readonly actorExternalId: string;
  readonly actorLabel: string;
  readonly actorKind: string;
  readonly totalAllocation: number;
  readonly assignmentCount: number;
  readonly hasUnspecifiedAllocation: boolean;
  readonly status: AllocationLoadStatus;
  readonly assignments: readonly OrgAllocationAssignment[];
}

export interface OrgAllocationTargetRoster {
  readonly targetExternalId: string;
  readonly targetLabel: string;
  readonly targetKind: string;
  readonly totalAllocation: number;
  readonly peopleCount: number;
  readonly hasUnspecifiedAllocation: boolean;
  readonly assignments: readonly OrgAllocationAssignment[];
}

export type OrgProjectAllocationRoster = OrgAllocationTargetRoster;

export interface OrgAllocationView {
  readonly asOf: string;
  readonly capacityTarget: number;
  readonly assignments: readonly OrgAllocationAssignment[];
  readonly actors: readonly OrgActorAllocationLoad[];
  readonly people: readonly OrgPersonAllocationLoad[];
  readonly targets: readonly OrgAllocationTargetRoster[];
  /** Compatibility alias for the first shipped Atlas projection. */
  readonly projects: readonly OrgProjectAllocationRoster[];
  readonly summary: {
    readonly assignmentCount: number;
    readonly actorCount: number;
    readonly peopleCount: number;
    readonly targetCount: number;
    readonly projectCount: number;
    readonly overAllocatedPeople: number;
    readonly underAllocatedPeople: number;
    readonly unknownPeople: number;
    readonly overAllocatedActors: number;
    readonly underAllocatedActors: number;
    readonly unknownActors: number;
  };
}

export interface OrgAllocationViewOptions {
  readonly asOf?: Date | string;
  readonly capacityTarget?: number;
  readonly relationshipTypes?: readonly string[];
  readonly targetKinds?: readonly string[];
}

const PROJECT_ASSIGNMENT = 'project_assignment';
const CAPACITY_COMMITMENT = 'capacity_commitment';
const DEFAULT_WORKLOAD_RELATIONSHIP_TYPES = [PROJECT_ASSIGNMENT, CAPACITY_COMMITMENT] as const;

/**
 * Pure Atlas allocation read model over shared relationship folder records.
 *
 * The service intentionally consumes generic relationship records rather than
 * orgchart canvas edges. Project allocation is the first capacity-commitment
 * projection, but the row shape carries targetKind so future process/objective
 * or system allocations can reuse the same path.
 */
export class OrgAllocationService {
  relationshipRecordToAssignment(record: FolderRecord): OrgAllocationAssignment | null {
    return toAssignment(record);
  }

  suggestAllocationAsOf(
    relationshipRecords: readonly FolderRecord[],
    options: OrgAllocationViewOptions = {},
  ): Date | null {
    return suggestAllocationAsOf(relationshipRecords, options);
  }

  buildAllocationView(
    relationshipRecords: readonly FolderRecord[],
    options: OrgAllocationViewOptions = {},
  ): OrgAllocationView {
    const asOf = toDate(options.asOf) ?? new Date();
    const capacityTarget = clampCapacityTarget(options.capacityTarget);
    const assignments = assignmentsForOptions(relationshipRecords, options)
      .filter((assignment) => isActiveAt(assignment, asOf));

    const actors = Array.from(groupBy(assignments, (assignment) => assignment.actorExternalId))
      .map(([actorExternalId, rows]) => buildActorLoad(actorExternalId, rows, capacityTarget))
      .sort((a, b) => a.actorLabel.localeCompare(b.actorLabel));
    const people = actors
      .filter((actor) => actor.actorKind === 'person' || actor.actorKind === 'employee')
      .map((actor): OrgPersonAllocationLoad => ({
        personExternalId: actor.actorExternalId,
        personLabel: actor.actorLabel,
        totalAllocation: actor.totalAllocation,
        assignmentCount: actor.assignmentCount,
        hasUnspecifiedAllocation: actor.hasUnspecifiedAllocation,
        status: actor.status,
        assignments: actor.assignments,
      }));
    const targets = Array.from(groupBy(assignments, (assignment) => assignment.targetExternalId))
      .map(([targetExternalId, rows]) => buildTargetRoster(targetExternalId, rows))
      .sort((a, b) => a.targetLabel.localeCompare(b.targetLabel));

    return {
      asOf: asOf.toISOString(),
      capacityTarget,
      assignments,
      actors,
      people,
      targets,
      projects: targets,
      summary: {
        assignmentCount: assignments.length,
        actorCount: actors.length,
        peopleCount: people.length,
        targetCount: targets.length,
        projectCount: targets.filter((target) => target.targetKind === 'project').length,
        overAllocatedPeople: people.filter((person) => person.status === 'over_allocated').length,
        underAllocatedPeople: people.filter((person) => person.status === 'under_allocated').length,
        unknownPeople: people.filter((person) => person.status === 'unknown').length,
        overAllocatedActors: actors.filter((actor) => actor.status === 'over_allocated').length,
        underAllocatedActors: actors.filter((actor) => actor.status === 'under_allocated').length,
        unknownActors: actors.filter((actor) => actor.status === 'unknown').length,
      },
    };
  }

  buildProjectAllocationView(
    relationshipRecords: readonly FolderRecord[],
    options: OrgAllocationViewOptions = {},
  ): OrgAllocationView {
    return this.buildAllocationView(relationshipRecords, {
      ...options,
      relationshipTypes: [PROJECT_ASSIGNMENT],
      targetKinds: options.targetKinds ?? ['project'],
    });
  }
}

function assignmentsForOptions(
  relationshipRecords: readonly FolderRecord[],
  options: OrgAllocationViewOptions,
): OrgAllocationAssignment[] {
  const relationshipTypes = allowedSet(options.relationshipTypes ?? DEFAULT_WORKLOAD_RELATIONSHIP_TYPES);
  const targetKinds = allowedSet(options.targetKinds);
  return relationshipRecords
    .filter((record) => relationshipTypes.size === 0 ||
      relationshipTypes.has(relationshipType(record) ?? ''))
    .map((record) => toAssignment(record))
    .filter((assignment): assignment is OrgAllocationAssignment =>
      assignment !== null &&
      (targetKinds.size === 0 || targetKinds.has(assignment.targetKind)),
    );
}

function suggestAllocationAsOf(
  relationshipRecords: readonly FolderRecord[],
  options: OrgAllocationViewOptions,
): Date | null {
  const reference = toDate(options.asOf) ?? new Date();
  const assignments = assignmentsForOptions(relationshipRecords, options);
  if (assignments.length === 0) return null;
  if (assignments.some((assignment) => isActiveAt(assignment, reference))) return reference;

  const futureStarts = uniqueDateTimes(assignments
    .map((assignment) => toDate(assignment.validFrom))
    .filter((date): date is Date => !!date && date.getTime() >= reference.getTime()))
    .sort((left, right) => left.getTime() - right.getTime());
  const pastBoundaries = uniqueDateTimes(assignments
    .flatMap((assignment) => [toDate(assignment.validFrom), toDate(assignment.validTo)])
    .filter((date): date is Date => !!date && date.getTime() < reference.getTime()))
    .sort((left, right) => right.getTime() - left.getTime());

  for (const candidate of [...futureStarts, ...pastBoundaries]) {
    if (assignments.some((assignment) => isActiveAt(assignment, candidate))) return candidate;
  }
  return null;
}

function toAssignment(record: FolderRecord): OrgAllocationAssignment | null {
  const props = record.universalProps;
  const personExternalId = stringProp(props.subjectExternalId) ?? stringProp(props.subjectRecordId);
  const targetExternalId = stringProp(props.objectExternalId) ?? stringProp(props.objectRecordId);
  if (!personExternalId || !targetExternalId) return null;

  const allocation = readAllocation(props);
  const actorKind = stringProp(props.subjectType) ?? stringProp(props.actorKind) ?? 'person';
  const personLabel = stringProp(props.subjectLabel) ?? personExternalId;
  const targetKind = stringProp(props.targetKind) ?? stringProp(props.objectType) ?? 'project';
  const targetLabel = sourceRowTargetLabel(props) ??
    stringProp(props.objectLabel) ??
    targetExternalId;
  return {
    relationshipRecordId: record.id,
    relationshipExternalId: record.externalId,
    actorExternalId: personExternalId,
    actorLabel: personLabel,
    actorKind,
    personExternalId,
    personLabel,
    targetExternalId,
    targetLabel,
    targetKind,
    allocation,
    hasUnspecifiedAllocation: allocation == null,
    effort: readNumber(props.effort) ?? readNumber(isRecord(props.qualifiers) ? props.qualifiers.effort : undefined),
    effortUnit: stringProp(props.effortUnit) ?? stringProp(isRecord(props.qualifiers) ? props.qualifiers.effortUnit : undefined),
    role: stringProp(props.role) ?? stringProp(isRecord(props.qualifiers) ? props.qualifiers.role : undefined),
    validFrom: stringProp(props.validFrom),
    validTo: stringProp(props.validTo),
    basis: stringProp(props.basis),
    lifecycleState: lifecycleState(props.lifecycle),
    recordStatus: stringProp(props.status) ?? stringProp(props.recordStatus),
    sourceStatus: stringProp(isRecord(props.source) ? props.source.status : undefined),
    sourceBindingStatus: stringProp(isRecord(props._sourceBinding) ? props._sourceBinding.status : undefined),
    confidence: readConfidence(props),
    source: isRecord(props.source) ? props.source : {},
  };
}

function sourceRowTargetLabel(props: Record<string, unknown>): string | null {
  const source = isRecord(props.source) ? props.source : null;
  const rowSnapshot = source && isRecord(source.rowSnapshot) ? source.rowSnapshot : null;
  if (!rowSnapshot) return null;

  return firstStringValue(rowSnapshot, [
    'Target Name',
    'Target',
    'Project Name',
    'Project',
    'Work Context Name',
    'Work Context',
    'Process Name',
    'Service Name',
    'System Name',
    'Initiative Name',
  ]);
}

function buildActorLoad(
  actorExternalId: string,
  assignments: readonly OrgAllocationAssignment[],
  capacityTarget: number,
): OrgActorAllocationLoad {
  const totalAllocation = sumKnownAllocations(assignments);
  const hasUnspecifiedAllocation = assignments.some((assignment) => assignment.hasUnspecifiedAllocation);
  return {
    actorExternalId,
    actorLabel: assignments[0]?.actorLabel ?? actorExternalId,
    actorKind: assignments[0]?.actorKind ?? 'actor',
    totalAllocation,
    assignmentCount: assignments.length,
    hasUnspecifiedAllocation,
    status: loadStatus(totalAllocation, hasUnspecifiedAllocation, capacityTarget),
    assignments,
  };
}

function buildTargetRoster(
  targetExternalId: string,
  assignments: readonly OrgAllocationAssignment[],
): OrgAllocationTargetRoster {
  const people = new Set(assignments.map((assignment) => assignment.actorExternalId));
  return {
    targetExternalId,
    targetLabel: assignments[0]?.targetLabel ?? targetExternalId,
    targetKind: assignments[0]?.targetKind ?? 'project',
    totalAllocation: sumKnownAllocations(assignments),
    peopleCount: people.size,
    hasUnspecifiedAllocation: assignments.some((assignment) => assignment.hasUnspecifiedAllocation),
    assignments,
  };
}

function loadStatus(
  totalAllocation: number,
  hasUnspecifiedAllocation: boolean,
  capacityTarget: number,
): AllocationLoadStatus {
  if (hasUnspecifiedAllocation) return 'unknown';
  if (totalAllocation > capacityTarget + 0.0001) return 'over_allocated';
  if (Math.abs(totalAllocation - capacityTarget) <= 0.0001) return 'fully_allocated';
  return 'under_allocated';
}

function sumKnownAllocations(assignments: readonly OrgAllocationAssignment[]): number {
  return assignments.reduce((sum, assignment) => sum + (assignment.allocation ?? 0), 0);
}

function relationshipType(record: FolderRecord): string | null {
  return stringProp(record.universalProps.relationshipType) ?? stringProp(record.universalProps.predicate);
}

function readAllocation(props: Record<string, unknown>): number | null {
  const direct = normalizeAllocation(props.allocation);
  if (direct != null) return direct;
  const qualifiers = isRecord(props.qualifiers) ? props.qualifiers : {};
  return normalizeAllocation(qualifiers.allocation);
}

function readConfidence(props: Record<string, unknown>): number | null {
  const direct = normalizeConfidence(props.confidence);
  if (direct != null) return direct;
  const qualifiers = isRecord(props.qualifiers) ? props.qualifiers : {};
  const qualified = normalizeConfidence(qualifiers.confidence);
  if (qualified != null) return qualified;
  const source = isRecord(props.source) ? props.source : {};
  return normalizeConfidence(source.confidence);
}

function normalizeConfidence(value: unknown): number | null {
  if (value == null || value === '') return null;
  const raw = typeof value === 'string'
    ? value.trim().replace(/%$/, '')
    : value;
  const numeric = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(numeric)) return null;
  const ratio = numeric > 1 ? numeric / 100 : numeric;
  return Math.max(0, Math.min(1, ratio));
}

function normalizeAllocation(value: unknown): number | null {
  if (value == null || value === '') return null;
  const trimmed = typeof value === 'string' ? value.trim() : null;
  const hasPercentSuffix = trimmed?.endsWith('%') ?? false;
  const raw = trimmed ? trimmed.replace(/%$/, '') : value;
  const numeric = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(numeric)) return null;
  const ratio = hasPercentSuffix || numeric > 10 ? numeric / 100 : numeric;
  return Math.max(0, ratio);
}

function readNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function isActiveAt(assignment: OrgAllocationAssignment, asOf: Date): boolean {
  const start = toDate(assignment.validFrom);
  const end = toDate(assignment.validTo);
  if (start && start.getTime() > asOf.getTime()) return false;
  if (end && end.getTime() < asOf.getTime()) return false;
  return true;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function uniqueDateTimes(values: readonly Date[]): Date[] {
  const seen = new Set<number>();
  const out: Date[] = [];
  for (const value of values) {
    const time = value.getTime();
    if (seen.has(time)) continue;
    seen.add(time);
    out.push(value);
  }
  return out;
}

function clampCapacityTarget(value: number | undefined): number {
  if (!value || !Number.isFinite(value) || value <= 0) return 1;
  return Math.max(0.01, Math.min(10, value));
}

function allowedSet(values: readonly string[] | undefined): Set<string> {
  return new Set((values ?? []).map((value) => value.trim()).filter(Boolean));
}

function firstStringValue(
  row: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = stringProp(row[key]);
    if (value) return value;
  }
  return null;
}

function groupBy<T>(
  rows: readonly T[],
  keyFor: (row: T) => string,
): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const row of rows) {
    const key = keyFor(row);
    const bucket = groups.get(key) ?? [];
    bucket.push(row);
    groups.set(key, bucket);
  }
  return groups;
}

function stringProp(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function lifecycleState(value: unknown): string | null {
  if (typeof value === 'string') return stringProp(value);
  if (!isRecord(value)) return null;
  return stringProp(value.state);
}
